import React, { useState } from 'react';

interface ClientLoginProps {
  onLogin: (email: string, password: string) => void;
  onForgotPassword?: () => void;
  registrationEmail?: string;
}

const ClientLogin: React.FC<ClientLoginProps> = ({
  onLogin,
  onForgotPassword,
  registrationEmail
}) => {
  const [formData, setFormData] = useState({
    email: registrationEmail || '',
    password: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    general: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear errors when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = { email: '', password: '', general: '' };
    let isValid = true;

    if (!formData.email) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors(prev => ({ ...prev, general: '' }));

    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      onLogin(formData.email, formData.password);
    } catch (error) {
      setErrors(prev => ({
        ...prev,
        general: 'Invalid email or password. Please try again.'
      }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app">
      <div style={{
        maxWidth: '500px',
        margin: '0 auto',
        background: '#28527A',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ color: '#f9fafb', fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
            Welcome Back
          </h1>
          <p style={{ color: '#6CA6C1', margin: 0 }}>
            Sign in to complete your medical forms
          </p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit}>
          <div style={{
            background: '#113F67',
            borderRadius: '12px',
            padding: '2rem',
            border: '1px solid #6CA6C1'
          }}>
            {/* General Error */}
            {errors.general && (
              <div style={{
                background: '#7f1d1d',
                color: '#fca5a5',
                padding: '0.75rem',
                borderRadius: '8px',
                marginBottom: '1.5rem',
                fontSize: '0.875rem',
                textAlign: 'center'
              }}>
                {errors.general}
              </div>
            )}

            {/* Email Field */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                color: '#f9fafb',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                Email Address *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email address"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: errors.email ? '1px solid #ef4444' : '1px solid #374151',
                  background: '#374151',
                  color: '#f9fafb',
                  fontSize: '1rem'
                }}
              />
              {errors.email && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  {errors.email}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{
                display: 'block',
                color: '#f9fafb',
                marginBottom: '0.5rem',
                fontWeight: '500'
              }}>
                Password *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: errors.password ? '1px solid #ef4444' : '1px solid #374151',
                  background: '#374151',
                  color: '#f9fafb',
                  fontSize: '1rem'
                }}
              />
              {errors.password && (
                <p style={{ color: '#ef4444', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  {errors.password}
                </p>
              )}
            </div>

            {/* Forgot Password Link */}
            {onForgotPassword && (
              <div style={{ textAlign: 'right', marginBottom: '1.5rem' }}>
                <button
                  type="button"
                  onClick={onForgotPassword}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6CA6C1',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    textDecoration: 'underline'
                  }}
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                background: isLoading
                  ? '#6b7280'
                  : 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)',
                color: 'white',
                padding: '0.75rem',
                fontSize: '1rem',
                fontWeight: '600',
                border: 'none',
                borderRadius: '8px',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                boxShadow: isLoading
                  ? 'none'
                  : '0 4px 14px 0 rgba(212, 160, 23, 0.3)'
              }}
              onMouseEnter={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px 0 rgba(212, 160, 23, 0.4)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isLoading) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 14px 0 rgba(212, 160, 23, 0.3)';
                }
              }}
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
          </div>
        </form>

        {/* Help Text */}
        <div style={{
          textAlign: 'center',
          marginTop: '1.5rem',
          padding: '1rem',
          background: '#065f46',
          borderRadius: '8px',
          border: '1px solid #10b981'
        }}>
          <p style={{
            color: '#d1d5db',
            fontSize: '0.875rem',
            margin: 0,
            lineHeight: '1.5'
          }}>
            New to our portal? Your account was just created during registration.
            Use the email and password you set up to access your medical forms.
          </p>
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '2rem',
          paddingTop: '1rem',
          borderTop: '1px solid #374151'
        }}>
          <p style={{
            color: '#d1d5db',
            fontSize: '0.875rem',
            margin: 0
          }}>
            © 2026 My ClinicCore | <span style={{ color: '#6CA6C1', cursor: 'pointer' }}>BAA</span> | <span style={{ color: '#6CA6C1', cursor: 'pointer' }}>Terms</span> | <span style={{ color: '#6CA6C1', cursor: 'pointer' }}>Privacy</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ClientLogin;